
import React from 'react';
import ModernPolicyLayout from '@/components/legal/ModernPolicyLayout';

const PrivacyPolicy = () => {
  const tableOfContents = [
    { id: 'introduction', title: 'Introduction', level: 1 },
    { id: 'data-collection', title: 'Data We Collect', level: 1 },
    { id: 'identity-data', title: 'Identity Data', level: 2 },
    { id: 'financial-data', title: 'Financial Data', level: 2 },
    { id: 'technical-data', title: 'Technical Data', level: 2 },
    { id: 'data-usage', title: 'How We Use Your Data', level: 1 },
    { id: 'data-security', title: 'Data Security', level: 1 },
    { id: 'data-retention', title: 'Data Retention', level: 1 },
    { id: 'your-rights', title: 'Your Legal Rights', level: 1 },
    { id: 'gdpr-rights', title: 'GDPR Rights (EU)', level: 2 },
    { id: 'ccpa-rights', title: 'CCPA Rights (California)', level: 2 },
    { id: 'third-party-services', title: 'Third-Party Service Providers', level: 1 },
    { id: 'cookies', title: 'Cookies & Tracking', level: 1 },
    { id: 'children', title: "Children's Privacy", level: 1 },
    { id: 'international', title: 'International Data Transfers', level: 1 },
    { id: 'breach', title: 'Data Breach Notification', level: 1 },
    { id: 'state-laws', title: 'U.S. State Privacy Laws', level: 1 },
    { id: 'third-party', title: 'Third-Party Links', level: 1 },
    { id: 'changes', title: 'Policy Changes', level: 1 },
    { id: 'contact', title: 'Contact Information', level: 1 }
  ];

  return (
    <ModernPolicyLayout 
      title="Privacy Policy" 
      estimatedReadTime="20 min read"
      lastUpdated="Last updated: October 10, 2025"
      tableOfContents={tableOfContents}
    >
      <div className="space-y-8">
        <section id="introduction">
          <h2>1. Introduction</h2>
          <p>
            At EquiForge™, we respect your privacy and are committed to protecting your personal data. 
            This privacy policy will inform you about how we look after your personal data when you use 
            our mobile application and tell you about your privacy rights.
          </p>
        </section>

        <section id="data-collection">
          <h2>2. The Data We Collect</h2>
          <p>
            We may collect, use, store and transfer different kinds of personal data about you which 
            we have grouped as follows:
          </p>

          <div id="identity-data" className="mt-6">
            <h3>Identity Data</h3>
            <p>
              Includes first name, last name, username or similar identifier that helps us personalize 
              your experience within the EquiForge™ platform.
            </p>
          </div>

          <div id="financial-data" className="mt-6">
            <h3>Financial Data</h3>
            <p>
              Includes data you enter into our calculators such as income, debt obligations, housing 
              preferences, and credit information. This data is used solely for calculation purposes 
              and is encrypted both in transit and at rest.
            </p>
          </div>

          <div id="technical-data" className="mt-6">
            <h3>Technical Data</h3>
            <p>
              Includes internet protocol (IP) address, your login data, browser type and version, 
              time zone setting and location, operating system and platform and other technology 
              on the devices you use to access this app.
            </p>
          </div>
        </section>

        <section id="data-usage">
          <h2>3. How We Use Your Data</h2>
          <p>
            We use your data to provide you with the app's functionality, improve our service, 
            communicate with you, and comply with legal obligations. Specifically, we use your data to:
          </p>
          <ul>
            <li>Provide mortgage and buying power calculations based on your inputs</li>
            <li>Remember your preferences and settings for a personalized experience</li>
            <li>Analyze usage patterns to improve the app's functionality and user experience</li>
            <li>Communicate important updates about the app and new features</li>
            <li>Ensure security and prevent fraudulent activities</li>
          </ul>
        </section>

        <section id="data-security">
          <h2>4. Data Security</h2>
          <p>
            We have put in place appropriate security measures to prevent your personal data from 
            being accidentally lost, used or accessed in an unauthorized way, altered or disclosed. 
            Our security measures include:
          </p>
          <ul>
            <li><strong>Encryption:</strong> All data is encrypted in transit using TLS 1.3 and at rest using AES-256</li>
            <li><strong>Access Controls:</strong> Strict access controls with multi-factor authentication for all team members</li>
            <li><strong>Regular Audits:</strong> Security audits conducted quarterly by independent third parties</li>
            <li><strong>Incident Response:</strong> 24/7 monitoring with immediate incident response procedures</li>
          </ul>
        </section>

        <section id="data-retention">
          <h2>5. Data Retention</h2>
          <p>
            We will only retain your personal data for as long as necessary to fulfill the purposes 
            we collected it for, including for the purposes of satisfying any legal, accounting, or 
            reporting requirements. Account data is typically retained for the duration of your 
            account plus 7 years for compliance purposes.
          </p>
        </section>

        <section id="your-rights">
          <h2>6. Your Legal Rights</h2>
          <p>
            Under certain circumstances, you have rights under data protection laws in relation to 
            your personal data. The specific rights available to you depend on your location and 
            applicable laws.
          </p>

          <div id="gdpr-rights" className="mt-6">
            <h3>GDPR Rights (European Union)</h3>
            <p>
              If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, 
              you have the following rights under the General Data Protection Regulation (GDPR):
            </p>
            <ul>
              <li>
                <strong>Right to Access (Article 15):</strong> You have the right to request confirmation 
                of whether we process your personal data and, if so, to receive a copy of that data along 
                with information about how it is processed.
              </li>
              <li>
                <strong>Right to Rectification (Article 16):</strong> You have the right to request 
                correction of inaccurate personal data and to have incomplete data completed.
              </li>
              <li>
                <strong>Right to Erasure/"Right to be Forgotten" (Article 17):</strong> You have the right 
                to request deletion of your personal data under certain circumstances, such as when the data 
                is no longer necessary for its original purpose or when you withdraw consent.
              </li>
              <li>
                <strong>Right to Restriction of Processing (Article 18):</strong> You have the right to 
                request that we limit the processing of your personal data in certain situations, such as 
                when you contest the accuracy of the data.
              </li>
              <li>
                <strong>Right to Data Portability (Article 20):</strong> You have the right to receive 
                your personal data in a structured, commonly used, machine-readable format and to transmit 
                that data to another controller.
              </li>
              <li>
                <strong>Right to Object (Article 21):</strong> You have the right to object to processing 
                of your personal data based on legitimate interests, direct marketing, or for research purposes.
              </li>
              <li>
                <strong>Rights Related to Automated Decision-Making (Article 22):</strong> You have the right 
                not to be subject to decisions based solely on automated processing, including profiling, 
                which produce legal effects or similarly significantly affect you.
              </li>
            </ul>
            <p>
              <strong>How to Exercise Your GDPR Rights:</strong> To exercise any of these rights, please 
              contact us at privacy@equiforge.com with "GDPR Request" in the subject line. We will respond 
              to your request within one month, though this period may be extended by two additional months 
              in complex cases. We may request additional information to verify your identity before processing 
              your request.
            </p>
            <p>
              <strong>Right to Lodge a Complaint:</strong> You have the right to lodge a complaint with a 
              supervisory authority, in particular in the EU member state where you reside, work, or where 
              an alleged infringement occurred.
            </p>
          </div>

          <div id="ccpa-rights" className="mt-6">
            <h3>CCPA Rights (California)</h3>
            <p>
              If you are a California resident, you have specific rights under the California Consumer 
              Privacy Act (CCPA) and its successor, the California Privacy Rights Act (CPRA):
            </p>
            <ul>
              <li>
                <strong>Right to Know:</strong> You have the right to request disclosure of the categories 
                and specific pieces of personal information we have collected about you, the categories of 
                sources from which we collected it, our business purposes for collecting it, and the categories 
                of third parties with whom we share it.
              </li>
              <li>
                <strong>Right to Delete:</strong> You have the right to request deletion of personal information 
                we have collected from you, subject to certain exceptions (e.g., to complete transactions, 
                detect security incidents, comply with legal obligations).
              </li>
              <li>
                <strong>Right to Correct:</strong> You have the right to request correction of inaccurate 
                personal information we maintain about you.
              </li>
              <li>
                <strong>Right to Opt-Out of Sale or Sharing:</strong> You have the right to opt out of the 
                sale or sharing of your personal information. <em>Note: EquiForge™ does not sell or share 
                personal information as defined by the CCPA.</em>
              </li>
              <li>
                <strong>Right to Limit Use of Sensitive Personal Information:</strong> You have the right to 
                limit our use and disclosure of sensitive personal information to purposes necessary to perform 
                services reasonably expected by consumers.
              </li>
              <li>
                <strong>Right to Non-Discrimination:</strong> You have the right not to receive discriminatory 
                treatment for exercising your CCPA rights. We will not deny goods or services, charge different 
                prices, or provide a different level of service for exercising your rights.
              </li>
            </ul>
            <p>
              <strong>How to Exercise Your CCPA Rights:</strong> To exercise any of these rights, please:
            </p>
            <ul>
              <li>Email us at privacy@equiforge.com with "CCPA Request" in the subject line</li>
              <li>Use our online request form (if available)</li>
              <li>Call us at our toll-free number (if available)</li>
            </ul>
            <p>
              <strong>Verification Process:</strong> We will verify your identity before processing your request 
              by matching information you provide with information we have on file. For deletion requests, we may 
              require additional verification. You may designate an authorized agent to make requests on your behalf, 
              but you must verify your identity and confirm the agent's authority.
            </p>
            <p>
              <strong>Response Timeline:</strong> We will respond to verifiable requests within 45 days, or notify 
              you if we need an additional 45 days to respond. If we cannot fulfill your request, we will explain why.
            </p>
          </div>
        </section>

        <section id="third-party-services">
          <h2>7. Third-Party Service Providers</h2>
          <p>
            EquiForge™ uses several third-party service providers to deliver our services. These providers 
            have access to your personal information only to perform specific tasks on our behalf and are 
            obligated not to disclose or use it for any other purpose.
          </p>

          <h3>Authentication & Identity Management</h3>
          <p>
            <strong>Supabase Auth (https://supabase.com):</strong> We use Supabase Authentication for user 
            authentication, identity verification, and account management. Supabase Auth processes your 
            email address, password (hashed), name, and authentication metadata. Supabase is SOC 2 Type II 
            certified and GDPR compliant.
          </p>
          <p>
            Data Shared: Email address, name, profile photo (if provided), authentication timestamps<br />
            Purpose: User authentication, session management, email verification<br />
            Privacy Policy: <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">https://supabase.com/privacy</a>
          </p>

          <h3>Payment Processing</h3>
          <p>
            <strong>Stripe (https://stripe.com):</strong> We use Stripe for payment processing and subscription 
            management. Your payment information is transmitted directly to Stripe and is never stored on our 
            servers. Stripe is PCI DSS Level 1 certified, the highest level of certification available in the 
            payments industry.
          </p>
          <p>
            Data Shared: Payment card information, billing address, email, transaction amount<br />
            Purpose: Processing payments, managing subscriptions, fraud prevention<br />
            Privacy Policy: <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a>
          </p>

          <h3>Database & Backend Infrastructure</h3>
          <p>
            <strong>Supabase (https://supabase.com):</strong> We use Supabase for database storage, real-time 
            data synchronization, and backend API services. Supabase stores your profile information, calculator 
            inputs, preferences, and usage data. Data is encrypted at rest using AES-256 and in transit using TLS 1.3.
          </p>
          <p>
            Data Shared: All user-provided data including profile, financial calculator inputs, preferences<br />
            Purpose: Data storage, real-time synchronization, backup and recovery<br />
            Data Location: AWS data centers (SOC 2 Type II certified)<br />
            Privacy Policy: <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">https://supabase.com/privacy</a>
          </p>

          <h3>Error Tracking & Performance Monitoring</h3>
          <p>
            <strong>Sentry (https://sentry.io):</strong> We use Sentry for error tracking, crash reporting, 
            and application performance monitoring. Sentry collects technical data about errors and performance 
            issues to help us identify and fix bugs quickly.
          </p>
          <p>
            Data Shared: Error messages, stack traces, device information, IP address (anonymized), user ID<br />
            Purpose: Error detection, debugging, performance optimization<br />
            Privacy Policy: <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer">https://sentry.io/privacy/</a>
          </p>

          <p className="mt-6">
            <strong>Third-Party Data Processing Agreement:</strong> All third-party service providers have 
            signed Data Processing Agreements (DPAs) with us and are contractually obligated to protect your 
            data and use it only for the specified purposes.
          </p>
        </section>

        <section id="cookies">
          <h2>8. Cookies & Tracking Technologies</h2>
          <p>
            EquiForge™ uses cookies and similar tracking technologies to enhance your experience, analyze 
            usage patterns, and improve our services. For detailed information about our cookie practices, 
            please see our <a href="/cookie-policy">Cookie Policy</a>.
          </p>

          <h3>Types of Cookies We Use</h3>
          <ul>
            <li>
              <strong>Essential Cookies:</strong> Required for the app to function properly. These cookies 
              enable core functionality such as authentication, security, and session management. You cannot 
              opt out of essential cookies.
            </li>
            <li>
              <strong>Performance & Analytics Cookies:</strong> Help us understand how visitors interact with 
              the app by collecting anonymous information about pages visited, time spent, and navigation paths.
            </li>
            <li>
              <strong>Functionality Cookies:</strong> Remember your preferences and settings to provide a 
              personalized experience, such as language preferences and saved calculator inputs.
            </li>
          </ul>

          <h3>Managing Cookies</h3>
          <p>
            You can control and manage cookies in your browser settings. Most browsers allow you to refuse 
            cookies or delete existing cookies. However, disabling cookies may affect the functionality of 
            the app and prevent you from using certain features.
          </p>
          <p>
            To learn more about cookies and how to manage them, visit: <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer">www.allaboutcookies.org</a>
          </p>
        </section>

        <section id="children">
          <h2>9. Children's Privacy</h2>
          <p>
            EquiForge™ Homebuyer Optimizer is not directed to children under the age of 13 (or 16 in the EEA). 
            We do not knowingly collect personal information from children under these ages.
          </p>
          <p>
            If you are a parent or guardian and believe that your child has provided us with personal information, 
            please contact us at privacy@equiforge.com. If we become aware that we have collected personal 
            information from a child without proper parental consent, we will take steps to delete that information 
            from our servers as quickly as possible.
          </p>
          <p>
            <strong>COPPA Compliance:</strong> EquiForge™ complies with the Children's Online Privacy Protection 
            Act (COPPA) and does not target services to children under 13.
          </p>
        </section>

        <section id="international">
          <h2>10. International Data Transfers</h2>
          <p>
            Your information may be transferred to, stored, and processed in countries other than your country 
            of residence, including the United States. These countries may have data protection laws that differ 
            from the laws of your country.
          </p>

          <h3>Data Processing Locations</h3>
          <p>
            EquiForge™ and our service providers process data in the following locations:
          </p>
          <ul>
            <li><strong>United States:</strong> Primary data processing and storage location</li>
            <li><strong>European Union:</strong> EU data centers for EU residents (where applicable)</li>
            <li><strong>Other Locations:</strong> Additional regions as needed for service delivery and redundancy</li>
          </ul>

          <h3>Safeguards for International Transfers</h3>
          <p>
            When transferring personal data internationally, we ensure appropriate safeguards are in place:
          </p>
          <ul>
            <li>
              <strong>Standard Contractual Clauses (SCCs):</strong> We use EU-approved Standard Contractual 
              Clauses for transfers from the EEA to countries without adequacy decisions.
            </li>
            <li>
              <strong>Adequacy Decisions:</strong> We transfer data to countries recognized by the European 
              Commission as providing adequate protection (e.g., UK, Switzerland).
            </li>
            <li>
              <strong>Data Processing Agreements:</strong> All third-party processors sign DPAs committing to 
              GDPR-equivalent protections regardless of location.
            </li>
            <li>
              <strong>Encryption:</strong> All data transfers are encrypted in transit using TLS 1.3 or higher.
            </li>
          </ul>

          <p>
            For more information about international transfers or to request a copy of the safeguards we have 
            in place, please contact privacy@equiforge.com.
          </p>
        </section>

        <section id="breach">
          <h2>11. Data Breach Notification</h2>
          <p>
            EquiForge™ takes data security seriously and has implemented comprehensive security measures to 
            protect your personal information. However, no method of transmission or storage is 100% secure.
          </p>

          <h3>Our Security Incident Response</h3>
          <p>
            In the event of a data breach that affects your personal information, we will:
          </p>
          <ul>
            <li>
              <strong>Assess the Breach:</strong> Immediately investigate the nature, scope, and impact of 
              the security incident
            </li>
            <li>
              <strong>Contain the Breach:</strong> Take immediate steps to contain the breach and prevent 
              further unauthorized access
            </li>
            <li>
              <strong>Notify Authorities:</strong> Report the breach to relevant supervisory authorities 
              within 72 hours as required by GDPR and other applicable laws
            </li>
            <li>
              <strong>Notify Affected Users:</strong> Inform you without undue delay if the breach is likely 
              to result in a high risk to your rights and freedoms
            </li>
            <li>
              <strong>Provide Remediation:</strong> Offer guidance on steps you can take to protect yourself 
              and, where appropriate, provide credit monitoring or identity theft protection services
            </li>
          </ul>

          <h3>Information We Will Provide</h3>
          <p>
            Our breach notification will include:
          </p>
          <ul>
            <li>Description of the nature of the breach</li>
            <li>Categories and approximate number of individuals affected</li>
            <li>Categories and approximate number of records affected</li>
            <li>Likely consequences of the breach</li>
            <li>Measures taken or proposed to address the breach</li>
            <li>Contact information for further inquiries</li>
          </ul>

          <p>
            If you suspect unauthorized access to your account, please contact us immediately at 
            security@equiforge.com.
          </p>
        </section>

        <section id="state-laws">
          <h2>12. U.S. State Privacy Laws</h2>
          <p>
            In addition to the CCPA (covered in Section 6), residents of other U.S. states may have 
            additional privacy rights under state-specific laws:
          </p>

          <h3>Virginia (VCDPA)</h3>
          <p>
            Virginia Consumer Data Protection Act grants Virginia residents rights similar to GDPR, including 
            access, correction, deletion, data portability, and opt-out of targeted advertising and profiling.
          </p>

          <h3>Colorado (CPA)</h3>
          <p>
            Colorado Privacy Act provides Colorado residents with rights to access, correct, delete, and 
            obtain a copy of their personal data, as well as opt out of targeted advertising and sale of 
            personal data.
          </p>

          <h3>Utah (UCPA)</h3>
          <p>
            Utah Consumer Privacy Act grants Utah residents rights to access, delete, and opt out of the 
            sale of personal data and targeted advertising.
          </p>

          <h3>Connecticut (CTDPA)</h3>
          <p>
            Connecticut Data Privacy Act provides Connecticut residents with rights to confirm, access, 
            correct, delete, and obtain a copy of their personal data, as well as opt out of processing 
            for targeted advertising, sale, or profiling.
          </p>

          <h3>Exercising State Privacy Rights</h3>
          <p>
            To exercise any rights under your state's privacy law, please contact us at privacy@equiforge.com 
            with your state name in the subject line. We will verify your residency and identity before 
            processing your request.
          </p>
        </section>

        <section id="third-party">
          <h2>13. Third-Party Links</h2>
          <p>
            This app may include links to third-party websites, plug-ins and applications. Clicking 
            on those links or enabling those connections may allow third parties to collect or share 
            data about you. We do not control these third-party websites and are not responsible for 
            their privacy statements.
          </p>
          <p>
            Before providing any information to third-party websites, we encourage you to review their 
            privacy policies and terms of service. EquiForge™ is not liable for the privacy practices 
            or content of third-party sites.
          </p>
        </section>

        <section id="changes">
          <h2>14. Changes to This Privacy Policy</h2>
          <p>
            We may update our privacy policy from time to time. We will notify you of any changes by 
            posting the new privacy policy on this page and updating the "Effective Date" at the top. 
            Significant changes will be communicated via email or in-app notification.
          </p>
        </section>

        <section id="contact">
          <h2>15. Contact Us</h2>
          <p>
            If you have any questions about this privacy policy or our privacy practices, please 
            contact us at: <strong>privacy@equiforge.com</strong>
          </p>
          <p>
            For urgent privacy concerns, you can reach our Data Protection Officer directly at: 
            <strong>dpo@equiforge.com</strong>
          </p>
        </section>
      </div>
    </ModernPolicyLayout>
  );
};

export default PrivacyPolicy;
